<script setup lang="ts">
import { computed, ref } from "vue";
import { getLegacyApp } from "../../core/legacy-bridge";
import { appStore } from "../../stores/app";

const summary = computed(() => appStore.summary);

// Ordering needs both boxes ticked
const termsAccepted = ref(false);
const disclaimerAccepted = ref(false);
const canOrder = computed(() => termsAccepted.value && disclaimerAccepted.value);

async function order() {
    if (!canOrder.value) return;
    await getLegacyApp().placeOrder();
    appStore.modal = "order";
}

function onAccessoryAmountChanged(productNumber: string, value: string) {
    getLegacyApp().onAccessoryAmountChanged(productNumber, Number(value));
}
</script>

<!-- Shown when appStore.page is "summary" (set by showPage in app.ts) -->
<template>
    <div class="summary-page page bulma-contextual" data-page="summary" :class="{ 'is-shown': appStore.page === 'summary' }">
        <div class="page__content columns is-gapless is-centered page__content--summary">
            <div class="column is-flex is-flex-direction-column">
                <section class="section p-5">
                    <h2 class="subtitle is-2 is-underscore summary__header">Summary</h2>
                </section>
                <div class="summary-content">

                    <section class="columns is-full is-flex-grow-1 bulma-contextual">
                        <div class="column">
                            <div class="section p-2 summary__description">

                                <div class="tile is-ancestor pl-5 pr-5 summary__description--wrapper">
                                    <div class="tile is-parent summary__description--image">
                                        <div class="is-flex is-justify-content-center is-align-items-center">
                                            <figure class="image is-flex is-justify-content-center is-align-items-center">
                                                <img class="summary-image" :src="summary?.image" />
                                            </figure>
                                        </div>
                                    </div>
                                </div>

                                <div class="tile is-ancestor pl-5 pr-5 summary__description--wrapper">
                                    <div class="tile is-parent summary__description--series">
                                        <div class="is-flex is-justify-content-center is-align-items-center">
                                            <figure class="image is-flex is-justify-content-center is-align-items-center">
                                                <img class="summary-image" :src="appStore.summaryThumbnail || undefined" />
                                            </figure>
                                        </div>
                                        <div class="is-flex is-centered is-vcentered">
                                            <div class="is-flex is-justify-content-center is-align-items-center p-5">
                                                <div class="summary__description--header mr-5 is-size-4"><span>Sofa series</span></div>
                                                <div class="summary__description--value is-size-4"><span v-html="summary?.seriesName"></span></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="tile is-parent summary__description--material">
                                        <div class="is-flex is-justify-content-center is-align-items-center">

                                            <figure class="image is-128x128 is-flex is-justify-content-center is-align-items-center">
                                                <img class="summary-image" :src="summary?.materialImage" />
                                            </figure>

                                            <div class="is-flex is-justify-content-center is-align-items-center p-5">
                                                <div class="summary__description--header mr-5 is-size-4"><span>Fabric selection</span></div>
                                                <div class="summary__description--value is-size-4"><span v-html="summary?.materialName"></span></div>
                                            </div>

                                        </div>
                                    </div>
                                </div>

                                <div class="tile is-ancestor pl-5 pr-5 summary__description--wrapper">
                                    <div class="tile is-parent summary__description--legs">
                                        <div class="is-flex is-justify-content-center is-align-items-center">
                                            <figure class="image is-128x128 is-flex is-justify-content-center is-align-items-center">
                                                <img class="summary-image" :src="summary?.legsImage" />
                                            </figure>
                                        </div>
                                        <div class="is-flex is-centered is-vcentered">
                                            <div class="is-flex is-justify-content-center is-align-items-center p-5">
                                                <div class="summary__description--header mr-5 is-size-4"><span>Legs</span></div>
                                                <div class="summary__description--value is-size-4"><span v-html="summary?.legsName"></span></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="tile is-parent summary__description--options">
                                        <div class="is-flex is-centered is-vcentered">
                                            <div class="is-flex is-justify-content-center is-align-items-center p-5">
                                                <div class="summary__description--header mr-5 is-size-4"><span>Options</span></div>
                                                <div class="summary__description--value is-size-4"><span v-html="summary?.optionsHtml"></span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>


                    <section class="columns is-full is-flex-grow-1 bulma-contextual">
                        <div class="column">
                            <div class="section p-5 summary__products">

                                <div class="summary__table">
                                    <div class="summary__table__header is-size-4">
                                        <span>Elements</span>
                                    </div>
                                    <div class="table-container">
                                        <table class="table is-fullwidth summary__table__rows">
                                            <tbody>
                                                <tr v-for="(row, r) in summary?.rows" :key="r" :class="row.className">
                                                    <td v-for="(cell, c) in row.cells" :key="c" :class="cell.className" :colspan="cell.colspan" v-html="cell.html"></td>
                                                </tr>
                                            </tbody>
                                            <tfoot>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div class="section p-5 summary__accessories">
                                <div class="summary__accessories__header is-size-4">
                                    <span>Accessories</span>
                                </div>
                                <div class="table-container">
                                    <table class="table is-fullwidth summary__accessories__list">
                                        <thead>
                                            <tr>
                                                <th colspan="5"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="accessory in summary?.accessories" :key="accessory.productNumber">
                                                <td v-html="accessory.name"></td>
                                                <td v-html="accessory.productNumber"></td>
                                                <td><input type="number" :value="accessory.amount" min="0" class="addon-amount" @change="onAccessoryAmountChanged(accessory.productNumber, ($event.target as HTMLInputElement).value)"><span> stk</span></td>
                                                <td>{{ accessory.price }}</td>
                                                <td>{{ accessory.total }}</td>
                                            </tr>
                                        </tbody>
                                        <tfoot>
                                            <tr v-if="summary">
                                                <td colspan="3"></td>
                                                <td><b>Total</b></td>
                                                <td><b>{{ summary.total }}</b></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                            </div>
                        </div>
                    </section>


                    <section class="columns is-full is-flex-grow-1 bulma-contextual">
                        <div class="column">

                            <div class="section p-2 summary__description">
                                <div class="tile is-ancestor pl-5 pr-5 summary__description--wrapper">
                                    <div class="tile is-parent summary__description--drawing">
                                        <div class="is-flex is-justify-content-center is-align-items-center">
                                            <figure class="image is-flex is-justify-content-center is-align-items-center">
                                                <img class="summary-image" :src="summary?.drawing" />
                                            </figure>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <div class="summary__actions p-5">
                    <div class="is-flex is-flex-direction-column">
                        <div class="is-flex is-justify-content-flex-end is-align-items-flex-start mb-4">
                            <label class="summary__actions__terms">
                                <input v-model="termsAccepted" type="checkbox" name="terms"/>
                                <span>
                                    I have read and understood the terms and conditions <a href="https://eilersen.eu/terms-and-condition" target="_blank">https://eilersen.eu/terms-and-condition</a>
                                </span>
                            </label>
                        </div>
                        <div class="is-flex is-justify-content-flex-end is-align-items-flex-start mb-4">
                            <label class="summary__actions__disclaimer">
                                <input v-model="disclaimerAccepted" type="checkbox" name="disclaimer" />
                                <span>
                                    The customer has been given the following information:<br>
                                    Colors and textures may appear differently on screen than in real life.<br>
                                    Please view physical fabric or leather  samples before making your final choice.<br>
                                    Digital images are for illustration only and may not reflect actual materials.
                                </span>
                            </label>
                        </div>
                    </div>
                    <nav class="level">
                        <div class="level-left">
                            <div class="summary__actions__confignumber level-item">
                                <span>Confignumber:</span>
                                <span class="summary__actions__confignumber--value">{{ summary?.configNumber }}</span>
                            </div>
                        </div>
                        <div class="level-right">
                            <div class="level-item">
                                <!-- Has never had an action -->
                                <button class="sharesave-button button is-default is-black-hover is-bordered is-radiusless">Share / save</button>
                            </div>
                            <div class="level-item">
                                <button class="order-button button is-default is-dark is-bordered is-radiusless has-icon" :disabled="!canOrder" @click="order">
                                    <span>Order</span>
                                    <span class="icon is-small is-right">
                                        <i class="fas fa-bag-shopping"></i>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </nav>
                </div>

            </div>
        </div>
    </div>
</template>
